import type { NodePropertySchema } from '../../../types/node-properties';

/**
 * MCP Tool Node Schema
 * Connects to MCP (Model Context Protocol) servers
 * Supports HTTP, SSE, and STDIO transports
 */
const schema: NodePropertySchema = {
  nodeType: 'mcp-tool',
  version: '1.0.0',
  
  properties: [
    // ==================================================================
    // TRANSPORT TYPE
    // ==================================================================
    {
      key: 'transportType',
      label: 'Transport Type',
      type: 'options',
      options: [
        { name: 'HTTP Streamable (SDK)', value: 'http' },
        { name: 'HTTP Simple', value: 'http-simple' },
        { name: 'Server-Sent Events (SSE)', value: 'sse' },
        { name: 'STDIO', value: 'stdio' }
      ],
      default: 'http',
      description: 'The transport protocol to use for MCP communication. HTTP Streamable (SDK) is recommended for compliant servers.'
    },
    
    // ==================================================================
    // HTTP/SSE CONFIGURATION
    // ==================================================================
    {
      key: 'mcpServerUrl',
      label: 'MCP Server URL',
      type: 'string',
      default: '',
      placeholder: 'https://mcp.example.com/sse',
      required: true,
      description: 'The URL of the MCP server endpoint',
      displayOptions: {
        show: {
          transportType: ['http', 'http-simple', 'sse']
        }
      },
      validation: [
        {
          type: 'required',
          message: 'MCP Server URL is required for HTTP/SSE transport'
        },
        {
          type: 'url',
          message: 'Please enter a valid URL'
        }
      ]
    },
    
    // ==================================================================
    // STDIO CONFIGURATION
    // ==================================================================
    {
      key: 'stdioCommand',
      label: 'Command',
      type: 'string',
      default: 'npx',
      placeholder: 'npx',
      required: true,
      description: 'The command to execute (e.g., npx, node, python)',
      displayOptions: {
        show: {
          transportType: ['stdio']
        }
      },
      validation: [
        {
          type: 'required',
          message: 'Command is required for STDIO transport'
        }
      ]
    },
    
    {
      key: 'stdioArgs',
      label: 'Arguments',
      type: 'json',
      default: ["-y", "@modelcontextprotocol/server-filesystem", "/tmp"],
      placeholder: '["-y", "@modelcontextprotocol/server-filesystem", "/tmp"]',
      description: 'Command arguments as a JSON array',
      displayOptions: {
        show: {
          transportType: ['stdio']
        }
      }
    },
    
    {
      key: 'stdioEnv',
      label: 'Environment Variables',
      type: 'json',
      default: {},
      placeholder: '{"API_KEY": "your-key"}',
      description: 'Environment variables as a JSON object',
      displayOptions: {
        show: {
          transportType: ['stdio']
        }
      }
    },
    
    // ==================================================================
    // AUTHENTICATION
    // ==================================================================
    {
      key: 'envVariables',
      label: 'Environment Variables',
      type: 'json',
      default: {},
      placeholder: '{"DATABRICKS_HOST": "https://...", "DATABRICKS_WAREHOUSE_ID": "..."}',
      description: 'Environment variables to pass to the MCP server (e.g., for Databricks configuration)',
      displayOptions: {
        show: {
          transportType: ['http', 'http-simple', 'sse']
        }
      }
    },
    {
      key: 'authType',
      label: 'Authentication',
      type: 'options',
      options: [
        { name: 'None', value: 'none' },
        { name: 'Bearer Token', value: 'bearer' },
        { name: 'Custom Header', value: 'header' }
      ],
      default: 'none',
      description: 'Authentication method for the MCP server',
      displayOptions: {
        show: {
          transportType: ['http', 'http-simple', 'sse']
        }
      }
    },
    
    {
      key: 'bearerToken',
      label: 'Bearer Token',
      type: 'string',
      default: '',
      placeholder: 'your-bearer-token',
      description: 'Bearer token for authentication',
      displayOptions: {
        show: {
          authType: ['bearer']
        }
      }
    },
    
    {
      key: 'customHeaderName',
      label: 'Header Name',
      type: 'string',
      default: 'X-API-Key',
      placeholder: 'X-API-Key',
      description: 'Custom header name for authentication',
      displayOptions: {
        show: {
          authType: ['header']
        }
      }
    },
    
    {
      key: 'customHeaderValue',
      label: 'Header Value',
      type: 'string',
      default: '',
      placeholder: 'your-api-key',
      description: 'Custom header value for authentication',
      displayOptions: {
        show: {
          authType: ['header']
        }
      }
    },
    
    // ==================================================================
    // TOOL SELECTION
    // ==================================================================
    {
      key: 'selectedTools',
      label: 'Available Tools',
      type: 'multiOptions',
      default: ['*'],
      placeholder: 'Select tools to expose to AI agents',
      description: 'Choose which MCP tools to make available. Default: All tools',
      // This will be handled by a custom component that fetches tools on-demand
      customComponent: 'MCPToolSelector'
    },
    
    // ==================================================================
    // ADVANCED OPTIONS
    // ==================================================================
    {
      key: 'timeout',
      label: 'Timeout (ms)',
      type: 'number',
      default: 60000,
      description: 'Request timeout in milliseconds',
      validation: [
        {
          type: 'min',
          value: 1000,
          message: 'Timeout must be at least 1000ms'
        },
        {
          type: 'max',
          value: 300000,
          message: 'Timeout cannot exceed 300000ms (5 minutes)'
        }
      ]
    },
    
    {
      key: 'retryOnFailure',
      label: 'Retry on Failure',
      type: 'boolean',
      default: false,
      description: 'Automatically retry failed requests'
    },
    
    {
      key: 'maxRetries',
      label: 'Max Retries',
      type: 'number',
      default: 3,
      description: 'Maximum number of retry attempts',
      displayOptions: {
        show: {
          retryOnFailure: [true]
        }
      },
      validation: [
        {
          type: 'min',
          value: 1,
          message: 'Must retry at least once'
        },
        {
          type: 'max',
          value: 10,
          message: 'Cannot exceed 10 retries'
        }
      ]
    }
  ]
};

export default schema;
